import React, {
  useEffect,
  useState
} from "react";

import {
  useNavigate
} from "react-router-dom";

const RentalHistory = () => {

  const navigate =
    useNavigate();

  const [rentals, setRentals] =
    useState([]);

  const [searchTerm, setSearchTerm] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("all");

  // =====================================
  // FETCH RENTALS
  // =====================================

  useEffect(() => {

    fetch(
      "/api/rental-history"
    )
      .then(res => res.json())
      .then(data => {

        setRentals(
          Array.isArray(data.rentals)
            ? data.rentals
            : []
        );

      })
      .catch(err => {

        console.error(err);

        setRentals([]);

      });

  }, []);

  // =====================================
  // FORMAT DATE
  // =====================================

  const formatDate = (date) => {

    if (!date) return "-";

    return new Date(date)
      .toLocaleDateString("en-GB");
  };

  // =====================================
  // FILTER
  // =====================================

  const filteredRentals =
    rentals.filter(r => {

      const matchesSearch =

        `${r.first_name || ""}
         ${r.last_name || ""}`
          .toLowerCase()
          .includes(
            searchTerm.toLowerCase()
          )

        ||

        (r.phone || "")
          .includes(searchTerm);

      if (!matchesSearch)
        return false;

      // =====================================
      // STATUS FILTERS
      // =====================================

      if (statusFilter === "all")
        return true;

      // PENDING PAYMENT

      if (
        statusFilter === "pending"
      ) {

        return (
          r.payment_status !== "paid"
        );

      }

      // OVERDUE

      if (
        statusFilter === "overdue"
      ) {

        return (

          r.status !== "returned"

          &&

          new Date(r.return_date)
          < new Date()

        );

      }

      return (
        r.status === statusFilter
      );

    });

  // =====================================
  // STATUS UI
  // =====================================

  const getStatusStyle = (
    status
  ) => {

    if (status === "returned") {

      return "bg-green-100 text-green-700";
    }

    if (
      status === "partial_return"
    ) {

      return "bg-yellow-100 text-yellow-700";
    }

    if (status === "active") {

      return "bg-blue-100 text-blue-700";
    }

    return "bg-red-100 text-red-700";
  };

  const getStatusText = (
    status
  ) => {

    if (status === "returned") {

      return "Returned";
    }

    if (
      status === "partial_return"
    ) {

      return "Partial Return";
    }

    if (status === "active") {

      return "Active";
    }

    return status;
  };

  // =====================================
  // PAYMENT STATUS UI
  // =====================================

  const getPaymentStyle = (
    status
  ) => {

    if (status === "paid") {

      return "bg-green-100 text-green-700";
    }

    if (status === "partial") {

      return "bg-yellow-100 text-yellow-700";
    }

    return "bg-red-100 text-red-700";
  };

  const getPaymentText = (
    status
  ) => {

    if (status === "paid") {

      return "Paid";
    }

    if (status === "partial") {

      return "Partial";
    }

    return "Unpaid";
  };

  return (

    <div className="min-h-screen bg-[#F5F7FB] p-4 md:p-6 space-y-6">

      {/* ========================================= */}
      {/* HERO */}
      {/* ========================================= */}

      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-5 md:p-8 text-white shadow-xl">

        <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-6">

          <div>

            <p className="uppercase tracking-widest text-indigo-200 text-sm">

              Rental Management

            </p>

            <h1 className="text-2xl md:text-4xl font-bold mt-2">

              Rental History

            </h1>

            <p className="mt-3 text-indigo-100">

              Manage all rentals, track statuses,
              pending settlements and overdue returns.

            </p>

          </div>

          {/* STATS */}

          <div className="hidden lg:flex gap-4">

            <div className="bg-white/10 backdrop-blur-md px-6 py-4 rounded-2xl border border-white/20 min-w-[180px]">

              <p className="text-sm text-indigo-100">

                Total Rentals

              </p>

              <h2 className="text-3xl font-bold mt-1">

                {rentals.length}

              </h2>

            </div>

            <div className="bg-white/10 backdrop-blur-md px-6 py-4 rounded-2xl border border-white/20 min-w-[180px]">

              <p className="text-sm text-indigo-100">

                Filtered Results

              </p>

              <h2 className="text-3xl font-bold mt-1">

                {filteredRentals.length}

              </h2>

            </div>

          </div>

        </div>

      </div>

      {/* ========================================= */}
      {/* FILTERS */}
      {/* ========================================= */}

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 md:p-6">

        <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-5">

          {/* SEARCH */}

          <div className="flex items-center gap-4 w-full lg:flex-1">

            <div className="w-12 h-12 rounded-2xl bg-indigo-100 flex items-center justify-center text-xl">

              🔍

            </div>

            <input
              type="text"
              placeholder="Search customer by name or phone..."
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(
                  e.target.value
                )
              }
              className="flex-1 border border-gray-200 bg-gray-50 rounded-2xl px-5 py-4 outline-none focus:ring-2 focus:ring-indigo-500"
            />

          </div>

          {/* FILTER BUTTONS */}

         <div className="flex gap-3 overflow-x-auto pb-2 whitespace-nowrap">

            {[
              {
                label: "All",
                value: "all",
                active:
                  "bg-gray-900 text-white",
                inactive:
                  "bg-gray-100 text-gray-700"
              },

              {
                label: "Active",
                value: "active",
                active:
                  "bg-blue-600 text-white",
                inactive:
                  "bg-blue-100 text-blue-700"
              },

              {
                label: "Partial",
                value: "partial_return",
                active:
                  "bg-yellow-500 text-white",
                inactive:
                  "bg-yellow-100 text-yellow-700"
              },

              {
                label: "Returned",
                value: "returned",
                active:
                  "bg-green-600 text-white",
                inactive:
                  "bg-green-100 text-green-700"
              },

              {
                label: "Pending",
                value: "pending",
                active:
                  "bg-orange-500 text-white",
                inactive:
                  "bg-orange-100 text-orange-700"
              },

              {
                label: "Overdue",
                value: "overdue",
                active:
                  "bg-red-600 text-white",
                inactive:
                  "bg-red-100 text-red-700"
              }

            ].map(tab => (

              <button
                key={tab.value}
                onClick={() =>
                  setStatusFilter(
                    tab.value
                  )
                }
               className={`flex-shrink-0 px-5 py-3 rounded-2xl text-sm font-semibold transition

                  ${
                    statusFilter ===
                    tab.value

                      ? tab.active

                      : tab.inactive
                  }
                `}
              >

                {tab.label}

              </button>

            ))}

          </div>

        </div>

      </div>

      {/* ========================================= */}
      {/* TABLE */}
      {/* ========================================= */}

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">

        {/* HEADER */}

        <div className="px-5 md:px-8 py-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between sm:items-center gap-4">

          <div>

            <h2 className="text-2xl font-bold text-gray-800">

              Rental Records

            </h2>

            <p className="text-sm text-gray-500 mt-1">

              View complete rental history and payment status

            </p>

          </div>

          <div className="bg-indigo-100 text-indigo-700 px-4 py-2 rounded-xl text-sm font-semibold">

            {filteredRentals.length}
            {" "}
            Rentals

          </div>

        </div>

        {/* TABLE */}

        <div className="overflow-x-auto">

          <table className="min-w-[850px] w-full">

            {/* HEAD */}

            <thead className="bg-gray-50 text-gray-500 text-sm">

              <tr>

                <th className="text-left px-8 py-5">

                  Rental ID

                </th>

                <th className="text-left px-6 py-5">

                  Customer

                </th>

                <th className="text-left px-6 py-5">

                  Phone

                </th>

                <th className="text-left px-6 py-5">

                  Rental Status

                </th>

                <th className="text-left px-6 py-5">

                  Payment Status

                </th>

                <th className="text-right px-8 py-5">

                  Actions

                </th>

              </tr>

            </thead>

            {/* BODY */}

            <tbody>

              {filteredRentals.length === 0 ? (

                <tr>

                  <td
                    colSpan="9"
                    className="py-20 text-center text-gray-500"
                  >

                    No rentals found

                  </td>

                </tr>

              ) : (

                filteredRentals.map(r => (

                  <tr
                    key={r.id}
                    className="border-t border-gray-100 hover:bg-gray-50 transition cursor-pointer"
                    onClick={() =>
                      navigate(
                        `/rental/${r.id}`
                      )
                    }
                  >

                    {/* RENTAL ID */}

                    <td className="px-8 py-6 font-bold text-gray-800">

                      #{r.id}

                    </td>

                    {/* CUSTOMER */}

                    <td className="px-6 py-6">

                     <div className="flex items-center gap-4 min-w-[180px]">

                        <div>

                          <p className="font-semibold text-gray-800">

                            {r.first_name}
                            {" "}
                            {r.last_name}

                          </p>

                          <p className="text-sm text-gray-500 mt-1">

                            Rental Customer

                          </p>

                        </div>

                      </div>

                    </td>

                    {/* PHONE */}

                    <td className="px-6 py-6 text-gray-700 font-medium">

                      {r.phone}

                    </td>

                    {/* RENTAL STATUS */}

                    <td className="px-6 py-6">

                      <span
                        className={`px-4 py-2 rounded-full text-xs font-semibold ${getStatusStyle(r.status)}`}
                      >

                        {getStatusText(r.status)}

                      </span>

                    </td>

                    {/* PAYMENT STATUS */}

                    <td className="px-6 py-6">

                      <span
                        className={`px-4 py-2 rounded-full text-xs font-semibold ${getPaymentStyle(r.payment_status)}`}
                      >

                        {getPaymentText(r.payment_status)}

                      </span>

                    </td>

                    {/* ACTION */}

                    <td className="px-8 py-6 text-right whitespace-nowrap">

                      <button
                        onClick={(e) => {

                          e.stopPropagation();

                          navigate(
                            `/rental/${r.id}`
                          );

                        }}
                        className="whitespace-nowrap bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-3 rounded-2xl font-medium hover:shadow-lg transition"
                      >

                        View Details

                      </button>

                    </td>

                  </tr>

                ))

              )}

            </tbody>

          </table>

        </div>

      </div>

    </div>

  );
};

export default RentalHistory;